"use client";

import { usePathname } from "next/navigation";
import { ComponentProps } from "react";
import { twMerge } from "tailwind-merge";

import { DesktopFollowButton } from "@/features/analysis/components/follow-button/DesktopFollowButton";
import { MobileFollowButton } from "@/features/analysis/components/follow-button/MobileFollowButton";
import { Lang } from "@/i18n-config";
import { formatSymbol } from "@/utils/i18n/format-symbol";
import { getTranslations } from "@/utils/i18n/get-translations";

import { SymbolInfo } from "../../services/symbol-info";
import SlidesOpenButton from "./SlidesOpenButton";

type SymbolTitleProps = {
  symbol: string;
  lang: Lang;
  data: SymbolInfo;
};

const PageCategoryNameMap = {
  overview: "overview",
  "whale-interest": "whaleInterest",
  "stock-sentiment": "stockSentiment",
  "financial-metrics": "financialMetrics",
  "ai-view": "aiView",
};

export const SymbolTitle = ({ symbol, lang, data }: SymbolTitleProps) => {
  const pathname = usePathname();
  const { t } = getTranslations(lang);

  const pageCategory = pathname.split("/")[5];
  const oriented = pageCategory
    ? PageCategoryNameMap[pageCategory as keyof typeof PageCategoryNameMap]
    : "overview";

  const pageName =
    t.analysis.shared[oriented as keyof typeof t.analysis.shared];

  return (
    <section className="flex justify-between gap-2 px-4 sm:gap-6 sm:px-0">
      <div className="flex flex-wrap gap-2">
        <h1 className="flex flex-col gap-2" aria-label="company name">
          <span className="text-2xl font-bold text-white @3xl:text-3xl">
            {data.name}{" "}
          </span>
          <span className="text-sm text-zinc-400">{`${formatSymbol(
            symbol
          )}.${data.country?.toUpperCase()} ${pageName}`}</span>
        </h1>
        <div className="flex h-fit gap-2 pt-2">
          <Badge aria-label="market" className="bg-sky-100 text-sky-800">
            Beta
          </Badge>
          <Badge aria-label="market">
            {t.analysis.overview.stock(data.country ?? "US")}
          </Badge>
          <Badge aria-label="category">{data.sector}</Badge>
        </div>
      </div>
      <div className="flex shrink-0 gap-2 self-end sm:self-start">
        <div>
          <SlidesOpenButton symbol={symbol} lang={lang} />
        </div>
        <div>
          <MobileFollowButton
            symbol={symbol ?? ""}
            className="mt-1 block @3xl:hidden"
          />
          <DesktopFollowButton
            symbol={symbol ?? ""}
            className="hidden @3xl:flex"
          />
        </div>
      </div>
    </section>
  );
};

const Badge = (props: ComponentProps<"span">) => (
  <span
    {...props}
    className={twMerge(
      "whitespace-nowrap rounded-full border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600",
      props.className
    )}
  >
    {props.children}
  </span>
);
